import {
  buildProxyEntryFromForm,
  getDefaultProxyFormState,
  getProxyEntryKey,
  normalizeRoutePrefix,
  validateProxyForm,
} from './proxyRouteUtils';

export const CONFIG_TABS = [
  { id: 'cors', label: 'CORS' },
  { id: 'frame', label: 'Frame' },
  { id: 'mime', label: 'MIME' },
  { id: 'proxy', label: 'Proxy' },
];

export const BROWSE_PAGE_SIZE = 10;
export const CORS_PAGE_SIZE = BROWSE_PAGE_SIZE;

export const resolveAuthor = (accounts = []) => {
  const username = accounts[0]?.username;
  if (username && username.includes('@')) {
    return username.split('@')[0];
  }
  if (username) {
    return username;
  }
  return localStorage.getItem('userOid') || '';
};

export const getEntryCount = (configId, document) => {
  if (!document?.data) {
    return 0;
  }
  if (configId === 'cors') {
    return document.data.domains?.length || 0;
  }
  if (configId === 'frame') {
    return document.data.patterns?.length || 0;
  }
  if (configId === 'mime') {
    return Object.keys(document.data.overrides || {}).length;
  }
  if (configId === 'proxy') {
    return Object.keys(document.data.migratedPrefixes || {}).length;
  }
  return 0;
};

export const buildEntryFromForm = (configId, formState) => {
  if (configId === 'mime') {
    return {
      extension: formState.extension,
      contentType: formState.contentType,
    };
  }
  if (configId === 'proxy') {
    return buildProxyEntryFromForm(formState);
  }
  if (configId === 'cors') {
    const entry = { pattern: formState.pattern };
    if (formState.category) {
      entry.category = formState.category;
    }
    entry.enabled = formState.enabled !== false;
    return entry;
  }
  return { pattern: formState.pattern };
};

export const getEntryKey = (configId, formState = {}) => {
  if (configId === 'mime') {
    return formState.extension?.trim() || '';
  }
  if (configId === 'proxy') {
    return getProxyEntryKey(formState);
  }
  return formState.pattern?.trim() || '';
};

export const getDefaultFormState = (configId) => {
  if (configId === 'mime') {
    return { extension: '', contentType: '' };
  }
  if (configId === 'proxy') {
    return getDefaultProxyFormState();
  }
  if (configId === 'cors') {
    return { pattern: '', category: '', enabled: true };
  }
  return { pattern: '' };
};

export const validateForm = (configId, action, formState) => {
  if (configId === 'mime') {
    if (!formState.extension?.trim()) {
      return 'Extension is required';
    }
    if (!formState.extension.startsWith('.')) {
      return 'Extension must start with a dot (e.g. .jsonp)';
    }
    if (action !== 'remove' && !formState.contentType?.trim()) {
      return 'Content-Type is required';
    }
    return null;
  }

  if (configId === 'proxy') {
    return validateProxyForm(formState, { operation: action });
  }

  if (!formState.pattern?.trim()) {
    return 'Pattern is required';
  }
  return null;
};

export const formatMetadataSummary = (metadata = []) => {
  if (!metadata.length) {
    return '';
  }
  return metadata
    .map((item) => {
      const label = item.configId?.toUpperCase() || '';
      return `${label} v${item.version ?? '?'}`;
    })
    .join(' · ');
};

/**
 * @param {object|null} propagation - MediaServer propagation payload
 * @param {{ successAction?: 'save' | 'update' }} [options]
 */
export const getPropagationMessage = (propagation, { successAction = 'save' } = {}) => {
  const verb = successAction === 'update' ? 'Updated' : 'Saved';
  if (!propagation) {
    return `${verb} successfully.`;
  }
  if (propagation.mode === 'restart' || propagation.requiresRestart) {
    return `${verb}. Fleet reload required before traffic sees this change.`;
  }
  const minutes = propagation.expectedFleetLatencyMinutes || '3-8';
  return `${verb}. Live across fleet in ~${minutes} minutes.`;
};

export const buildRemoveSuccessMessage = (configId, entry) => {
  const entryLabel = getEntryKey(configId, entry);
  return `The entry <strong>${entryLabel}</strong> has been removed from the runtime configuration. <br>`;
};

export const createEmptyDocument = (configId) => ({
  configId,
  version: 0,
  data: emptyDocumentData(configId),
});

const emptyDocumentData = (configId) => {
  if (configId === 'cors') {
    return { domains: [] };
  }
  if (configId === 'frame') {
    return { patterns: [] };
  }
  if (configId === 'proxy') {
    return { migratedPrefixes: {} };
  }
  return { overrides: {} };
};

const removeEntryByPreviousKey = (configId, previousKey) => {
  if (configId === 'mime') {
    return { extension: previousKey };
  }
  if (configId === 'proxy') {
    return { prefix: previousKey };
  }
  return { pattern: previousKey };
};

/** Update in-memory document after a successful POST (no GET refresh). */
export const applyLocalDocumentChange = (configId, document, operation, entry, previousKey) => {
  if (operation === 'merge' && previousKey) {
    const newKey = getEntryKey(configId, entry);
    if (newKey && newKey !== previousKey) {
      const removed = applyLocalDocumentChange(
        configId,
        document,
        'remove',
        removeEntryByPreviousKey(configId, previousKey)
      );
      return applyLocalDocumentChange(configId, removed, 'merge', entry);
    }
  }

  const base = document || { configId, version: 0, data: emptyDocumentData(configId) };
  const data = { ...base.data };

  if (configId === 'mime') {
    const overrides = { ...(data.overrides || {}) };
    if (operation === 'remove') {
      delete overrides[entry.extension];
    } else {
      overrides[entry.extension] = entry.contentType;
    }
    return { ...base, data: { ...data, overrides } };
  }

  if (configId === 'frame') {
    let patterns = [...(data.patterns || [])];
    if (operation === 'remove') {
      patterns = patterns.filter((p) => p !== entry.pattern);
    } else {
      const lookupKey = previousKey || entry.pattern;
      const idx = patterns.indexOf(lookupKey);
      if (idx >= 0) {
        patterns[idx] = entry.pattern;
      } else {
        patterns.push(entry.pattern);
      }
    }
    return { ...base, data: { ...data, patterns } };
  }

  if (configId === 'proxy') {
    const migrated = { ...(data.migratedPrefixes || {}) };
    const prefix = normalizeRoutePrefix(entry.prefix);
    if (operation === 'remove') {
      delete migrated[prefix];
    } else {
      migrated[prefix] = {
        target: entry.target,
        appendPhpRoute: entry.appendPhpRoute,
        migratedAt: entry.migratedAt || new Date().toISOString(),
      };
    }
    return { ...base, data: { ...data, migratedPrefixes: migrated } };
  }

  let domains = [...(data.domains || [])];
  if (operation === 'remove') {
    domains = domains.filter((d) => d.pattern !== entry.pattern);
  } else {
    const lookupKey = previousKey || entry.pattern;
    const idx = domains.findIndex((d) => d.pattern === lookupKey);
    const row = {
      pattern: entry.pattern,
      ...(entry.category !== undefined ? { category: entry.category } : {}),
      enabled: entry.enabled !== false,
    };
    if (idx >= 0) {
      domains[idx] = { ...domains[idx], ...row };
    } else {
      domains.push(row);
    }
  }
  return { ...base, data: { ...data, domains } };
};

/** Merge version info from POST results into metadata header state. */
export const mergeMetadataFromResults = (metadata = [], results = [], author) => {
  const byId = new Map(metadata.map((item) => [item.configId, { ...item }]));
  results.forEach((result) => {
    if (!result?.configId) {
      return;
    }
    byId.set(result.configId, {
      configId: result.configId,
      version: result.version,
      updatedAt: new Date().toISOString(),
      author: author || byId.get(result.configId)?.author,
      source: 'admin-api',
    });
  });
  return CONFIG_TABS.map((tab) => byId.get(tab.id)).filter(Boolean);
};
